import AdvancedTransactionTypes from "const/AdvancedTransactionTypes";
import Constants from "const/Constants";
import ContactSubTypes from "const/ContactSubTypes";
import Countries from "const/Countries";
import DataStatuses from "const/DataStatuses";
import Dates from "utils/Dates";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import DocumentTypes from "const/DocumentTypes";
import Documents from "utils/Documents";
import Money from "utils/Money";
import Objects from "utils/Objects";

const searchContact = (contactsData, { id, idNumber, vatId, name }, { paymentType, integrationService, xeroBusiness }) => {
  const buyPaymentType = paymentType === DocumentPaymentTypes.BUY;

  const contactData = contactsData.find((contact) => {
    if (paymentType && integrationService && !xeroBusiness) {
      if (
        (buyPaymentType && contact.subType !== ContactSubTypes.VENDOR)
          || (!buyPaymentType && contact.subType !== ContactSubTypes.CUSTOMER)
      ) {
        return false;
      }
    }
    if (id && id === contact.id) return true;

    return Objects.checkSimilarityBy({ idNumber, vatId, name }, contact);
  });

  return contactData;
};

export const getInitialState = (documentData, extraData) => {
  if (!documentData) return null;

  const defaultPayTo = { bankAccount: "", bankCode: "", bankName: "", serviceAccountId: "" };

  const { xeroBusiness, quickBooksBusiness, countryCode, currenciesData, usersData } = extraData;

  const {
    detailedTaxRates,
    paymentType,
    lineItems = [],
    recogniseData = {},
    createdBy = {},
    sourceData = {},
    updatedBy = {}
  } = documentData || {};

  const usCountry = countryCode === Countries.US;

  const precision = Money.getCurrencyPrecision(documentData.currency);

  const amountBase = Money.toMoneyNumber(documentData.amountBase, true, precision) || 0;

  const amountVat = Money.toMoneyNumber(documentData.amountVat, true, precision) || 0;

  const receiptType = documentData.type === DocumentTypes.RECEIPT;

  const buyPaymentType = paymentType === DocumentPaymentTypes.BUY;

  const { addresses: { recipient = {}, sender = {} } = {}, address } = recogniseData;

  const addressData = paymentType && (documentData.address || (buyPaymentType ? sender : recipient));

  const vatId = (countryCode === Countries.AU || countryCode === Countries.NZ)
    && (documentData.vatId || (buyPaymentType && (address?.vatId || sender?.vatId)));

  return {
    ...documentData,
    vatId: vatId || "",
    amountBase,
    amountVat,
    amount: Money.toMoneyNumber(amountBase + amountVat, true, precision),
    exportAs: documentData.exportAs || (quickBooksBusiness && receiptType ? AdvancedTransactionTypes.EXPENSE : ""),
    currency: currenciesData.some(({ code }) => code === documentData.currency) ? documentData.currency : "",
    paid: (
      (quickBooksBusiness && receiptType && documentData.exportAs === AdvancedTransactionTypes.EXPENSE)
      || !!documentData.paid
    ),
    payTo: Objects.getProps(documentData.payTo, Object.keys(defaultPayTo), defaultPayTo),
    createdBy: (createdBy && usersData.find(({ id }) => id === createdBy))
      || (sourceData?.from ? { email: sourceData.from } : null),
    updatedBy: usersData.find(({ id }) => id === updatedBy) || null,
    address: {
      ...((xeroBusiness || paymentType) ? addressData : {}),
      subType: (!xeroBusiness && paymentType)
        ? (paymentType === DocumentPaymentTypes.BUY ? ContactSubTypes.VENDOR : ContactSubTypes.CUSTOMER)
        : ""
    },
    detailedTaxRates: detailedTaxRates?.length ? detailedTaxRates
      : (quickBooksBusiness && !usCountry
        ? Documents.getDetailedTaxRates(paymentType, lineItems, documentData.currency)
        : []),
    lineItems: lineItems?.length ? lineItems.map((lineItem) => ({
      ...lineItem,
      vatRate: lineItem.vatRate || 0,
      item: lineItem.item?.id ? lineItem.item : undefined // TEMPORARY FIX
    })) : []
  };
};

export const recalculateLineItemFields = (prev, fieldName, value, currency) => {
  const precision = Money.getCurrencyPrecision(currency);

  switch (fieldName) {
    case "amountBase": {
      const { vatRate } = prev;

      const amountVat = value && vatRate
        ? Money.toMoneyNumber(value * vatRate / Constants.PERCENTS_MULTIPLIER, true, precision) : 0;

      const amountTotal = Money.toMoneyNumber((value || 0) + (amountVat || 0), true, precision);

      return { ...prev, [fieldName]: value, amountVat, amountTotal };
    }
    case "amountVat": {
      const { amountBase } = prev;

      const vatRate = value && amountBase ? Money.toMoneyNumber(value / amountBase * Constants.PERCENTS_MULTIPLIER) : 0;

      const amountTotal = Money.toMoneyNumber((amountBase || 0) + (value || 0), true, precision);

      return { ...prev, [fieldName]: value, amountTotal, vatRate };
    }
    case "vatRate": {
      const { amountBase } = prev;

      const amountVat = value && amountBase
        ? Money.toMoneyNumber(amountBase * value / Constants.PERCENTS_MULTIPLIER, true, precision)
        : 0;

      const amountTotal = Money.toMoneyNumber((amountBase || 0) + (amountVat || 0), true, precision);

      return { ...prev, [fieldName]: value, amountVat, amountBase, amountTotal };
    }
    case "taxRate": {
      const { amountBase } = prev;

      if (value?.id) {
        const amountVat = amountBase
          ? Money.toMoneyNumber(amountBase * value.rate / Constants.PERCENTS_MULTIPLIER, true, precision)
          : 0;

        const amountTotal = Money.toMoneyNumber((amountBase || 0) + (amountVat || 0), true, precision);

        return { ...prev, [fieldName]: value, vatRate: value.rate, amountVat, amountTotal };
      }

      return { ...prev, [fieldName]: value, vatRate: 0, amountVat: 0, amountTotal: amountBase };
    }
    case "category":
      return { ...prev, [fieldName]: value, item: undefined };
    case "item":
      return { ...prev, [fieldName]: value, category: undefined };
    default:
      return { ...prev, [fieldName]: value };
  }
};

export const updateState = (update, state, extraData) => {
  const {
    recogniseData,
    countryCode,
    quickBooksBusiness,
    xeroBusiness,
    integrationService,
    contactsData,
    status
  } = extraData;

  const keysToUpdate = Object.keys(update);

  const { lineItems, amountVat, amountBase, currency, paymentType, paymentDate, dueDate, issueDate } = state;

  const precision = Money.getCurrencyPrecision(update.currency || currency);

  const czCountry = countryCode === Countries.CZ;

  const usCountry = countryCode === Countries.US;

  keysToUpdate.forEach((key) => {
    switch (key) {
      case "amountBase": {
        update.amount = Money.toMoneyNumber((update.amountBase || 0) + (amountVat || 0), true, precision);
        if (lineItems.length === 1) {
          update.lineItems = [recalculateLineItemFields(
            lineItems[0], "amountBase", Money.toMoneyNumber(update.amountBase || 0, true, precision), currency
          )];
        }
        break;
      }
      case "amountVat": {
        update.amount = Money.toMoneyNumber((update.amountVat || 0) + (amountBase || 0), true, precision);
        if (lineItems.length === 1) {
          update.lineItems = [recalculateLineItemFields(
            lineItems[0], "amountVat", Money.toMoneyNumber(update.amountVat || 0, true, precision), currency
          )];
        }
        break;
      }
      case "amount": {
        update.amountBase = Money.toMoneyNumber(Math.max((update.amount || 0) - (state.amountVat || 0), 0), true, precision);
        if (lineItems.length === 1) {
          update.lineItems = [recalculateLineItemFields(
            lineItems[0], "amountBase", Money.toMoneyNumber(update.amountBase || 0, true, precision), currency
          )];
        }
        break;
      }
      case "payTo": {
        if (update.payTo?.serviceAccountId) {
          update.paid = true;
          if (!paymentDate && (dueDate || issueDate)) {
            update.paymentDate = Dates.formatApiDate(dueDate || issueDate);
          }
        }
        break;
      }
      case "exportAs": {
        update.paid = update.exportAs === AdvancedTransactionTypes.EXPENSE;
        update.payTo = {};
        break;
      }
      case "detailedTaxRates": {
        update.amountVat = update.detailedTaxRates
          .reduce((aggregator, currentValue) => aggregator + +(currentValue.value || 0), 0);
        update.amount = Money.toMoneyNumber((update.amountVat || 0) + (amountBase || 0), true, precision);
        break;
      }
      case "address": {
        update.vendorId = update.address.id;
        break;
      }
      case "paymentType": {
        const buyPaymentType = update.paymentType === DocumentPaymentTypes.BUY;

        const { payTo: { serviceAccountId, ...restPayTo } = {} } = state;

        const { addresses: { recipient = {}, sender = {} } = {} } = recogniseData;

        const nextAddress = buyPaymentType ? sender : recipient;

        const contactSubType = czCountry ? "" : (buyPaymentType ? ContactSubTypes.VENDOR : ContactSubTypes.CUSTOMER);

        update.exportAs = quickBooksBusiness && update.type === DocumentTypes.RECEIPT ? AdvancedTransactionTypes.EXPENSE : "";
        update.paid = !!(quickBooksBusiness && update.type === DocumentTypes.RECEIPT);
        update.payTo = restPayTo;
        update.linkedDocumentsIds = [];
        if (status === DataStatuses.TO_REVIEW) {
          const contactData = searchContact(contactsData, nextAddress, {
            paymentType: update.paymentType,
            integrationService,
            xeroBusiness
          });

          update.address = contactData || { ...nextAddress, subType: contactSubType };
          update.vendorId = contactData ? contactData.id : undefined;
        }
        if (quickBooksBusiness && !usCountry) {
          const detailedTaxRates = Documents.getDetailedTaxRates(update.paymentType, lineItems, currency);

          update.detailedTaxRates = detailedTaxRates;
          update.amountVat = detailedTaxRates.reduce((aggregator, currentValue) => aggregator + currentValue.value || 0, 0);
        }
        break;
      }
      case "lineItems": {
        if (!czCountry) {
          const prevLineItemsAmounts = Documents.calculateLineItemsData({
            lineItems, paymentType, countryCode, quickBooksBusiness, currency });

          const lineItemsAmounts = Documents.calculateLineItemsData({
            lineItems: update.lineItems, paymentType, countryCode, quickBooksBusiness, currency });

          if (prevLineItemsAmounts.amount !== lineItemsAmounts.amount) {
            update.amountBase = lineItemsAmounts.amountBase;
            update.amountVat = lineItemsAmounts.amountVat;
            update.amount = lineItemsAmounts.amount;
            update.tags = [
              ...new Set(
                lineItems.map(({ category, item }) => {
                  category = category || {};
                  item = item || {};

                  return (category.code || item.id) ? (category.name || item.name) : null;
                }).filter(Boolean)
              )
            ];
          }
          if (lineItemsAmounts.detailedTaxRates) {
            update.detailedTaxRates = lineItemsAmounts.detailedTaxRates;
          }
        }
        break;
      }
      default:
        break;
    }
  });

  return update;
};
